import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, onSnapshot, query, doc, updateDoc, getDoc } from 'firebase/firestore';
import { Search, Phone, Mail, User, MessageCircle, ExternalLink, Edit3, X, Save } from 'lucide-react';
import { notifySuccess, notifyError } from '../../utils/alerts';

const ListaAcudientes = () => {
    const [acudientes, setAcudientes] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [editModal, setEditModal] = useState(false);
    const [selectedAcu, setSelectedAcu] = useState(null);

    useEffect(() => {
        const q = query(collection(db, "ninos"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const todosLosAcudientes = [];

            snapshot.docs.forEach(doc => {
                const data = doc.data();
                if (data.acudientes && Array.isArray(data.acudientes)) {
                    data.acudientes.forEach((acu, idx) => {
                        todosLosAcudientes.push({
                            ...acu,
                            ninoAsociado: data.nombre,
                            gradoNino: data.grado,
                            idNino: doc.id,
                            indexInArray: idx
                        });
                    });
                }
            });
            setAcudientes(todosLosAcudientes);
        });
        return () => unsubscribe();
    }, []);

    const filtered = acudientes.filter(a =>
        a.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
        a.ninoAsociado.toLowerCase().includes(busqueda.toLowerCase()) ||
        a.telefono.includes(busqueda)
    );

    const abrirWhatsApp = (telefono) => {
        const num = telefono.replace(/\D/g, '');
        const finalNum = num.startsWith('57') ? num : `57${num}`;
        window.open(`https://wa.me/${finalNum}`, '_blank');
    };

    const openEdit = (acu) => {
        setSelectedAcu({ ...acu });
        setEditModal(true);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            const childRef = doc(db, "ninos", selectedAcu.idNino);
            const childSnap = await getDoc(childRef);
            
            if (childSnap.exists()) {
                const childData = childSnap.data();
                const nuevosAcudientes = [...childData.acudientes];
                
                // Actualizamos el acudiente en la posición correcta
                nuevosAcudientes[selectedAcu.indexInArray] = {
                    nombre: selectedAcu.nombre,
                    telefono: selectedAcu.telefono,
                    parentesco: selectedAcu.parentesco
                };

                await updateDoc(childRef, { acudientes: nuevosAcudientes });
                setEditModal(false);
                notifySuccess("Actualizado", "Información del acudiente actualizada.");
            }
        } catch (error) {
            console.error(error);
            notifyError("Error", "No se pudo actualizar la información.");
        }
    };

    return (
        <div className="space-y-4 sm:space-y-6 pb-20">
            <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-black text-gray-800 tracking-tight">Directorio de Acudientes</h1>
                    <p className="text-sm sm:text-base text-gray-500 font-medium">Contactos rápidos de padres y familiares.</p>
                </div>

                <div className="relative w-full sm:w-80">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Buscar acudiente o niño..."
                        className="w-full pl-12 pr-4 py-3 bg-white border border-gray-100 rounded-xl sm:rounded-2xl shadow-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm font-medium"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                    />
                </div>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {filtered.map((acu, index) => (
                    <div key={index} className="bg-white p-5 sm:p-6 rounded-2xl sm:rounded-[2.5rem] shadow-sm border border-gray-100 hover:shadow-md transition-all group">
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 sm:p-3 bg-blue-50 text-blue-600 rounded-xl sm:rounded-2xl group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                    <User size={18} className="sm:w-5 sm:h-5" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-800 text-sm sm:text-base">{acu.nombre}</h3>
                                    <span className="text-[9px] sm:text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-black uppercase tracking-tighter">
                                        {acu.parentesco}
                                    </span>
                                </div>
                            </div>

                            <button 
                                onClick={() => openEdit(acu)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors sm:opacity-0 group-hover:opacity-100"
                            >
                                <Edit3 size={18} />
                            </button>
                        </div>

                        <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                            <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-500 font-medium">
                                <MessageCircle size={14} className="text-green-500 sm:w-4 sm:h-4 shrink-0" />
                                {acu.telefono}
                            </div>
                            {/* <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-500 font-medium">
                                <Mail size={14} className="text-orange-400 sm:w-4 sm:h-4 shrink-0" />
                                <span className="truncate">{acu.correo}</span>
                            </div> */}
                            <div className="pt-3 border-t border-gray-50 mt-2 sm:mt-3">
                                <p className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase mb-0.5 sm:mb-1">Vinculado a:</p>
                                <p className="text-xs sm:text-sm font-bold text-gray-700">{acu.ninoAsociado} <span className="text-gray-400 font-normal">({acu.gradoNino}°)</span></p>
                            </div>
                        </div>

                        <a
                            href={`https://wa.me/${acu.telefono.toString().replace(/\D/g, '').startsWith('57') ? '' : '57'}${acu.telefono}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full flex items-center justify-center gap-2 py-2.5 sm:py-3 bg-green-50 text-green-600 rounded-xl font-bold hover:bg-green-500 hover:text-white transition-all active:scale-95 text-xs sm:text-sm"
                        >
                            <Phone size={14} className="sm:w-4 sm:h-4" /> WhatsApp Directo
                        </a>
                    </div>
                ))}
            </div>
            {filtered.length === 0 && (
                <div className="text-center p-8 bg-white rounded-2xl sm:rounded-[2.5rem] border border-gray-100 text-gray-400 font-medium text-sm sm:text-base">
                    No se encontraron acudientes con "<strong>{busqueda}</strong>".
                </div>
            )}

            {/* MODAL DE EDICIÓN */}
            {editModal && selectedAcu && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in" onClick={() => setEditModal(false)}>
                    <div className="bg-white w-full max-w-lg rounded-t-[2.5rem] sm:rounded-[3rem] shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 sm:slide-in-from-bottom-0 sm:zoom-in-95" onClick={e => e.stopPropagation()}>
                        <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mt-4 sm:hidden"></div>
                        
                        <div className="p-6 sm:p-8 border-b border-gray-100 flex justify-between items-center mt-2 sm:mt-0">
                            <div>
                                <h2 className="text-xl sm:text-2xl font-black text-gray-800 tracking-tight">Editar Acudiente</h2>
                                <p className="text-xs sm:text-sm text-gray-500 font-medium">Vinculado a: {selectedAcu.ninoAsociado}</p>
                            </div>
                            <button onClick={() => setEditModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors bg-gray-50">
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>

                        <form onSubmit={handleUpdate} className="p-6 sm:p-8 space-y-5 sm:space-y-6">
                            <div className="space-y-4">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-wider">Nombre Completo</label>
                                    <input 
                                        required
                                        className="w-full px-4 py-3 sm:p-4 bg-gray-50 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-bold border border-transparent focus:border-blue-200 focus:bg-white transition-all text-sm sm:text-base"
                                        value={selectedAcu.nombre} 
                                        onChange={e => setSelectedAcu({ ...selectedAcu, nombre: e.target.value })} 
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-wider">WhatsApp / Teléfono</label>
                                    <input 
                                        required
                                        className="w-full px-4 py-3 sm:p-4 bg-gray-50 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-bold border border-transparent focus:border-blue-200 focus:bg-white transition-all text-sm sm:text-base"
                                        value={selectedAcu.telefono} 
                                        onChange={e => setSelectedAcu({ ...selectedAcu, telefono: e.target.value })} 
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-wider">Parentesco</label>
                                    <input 
                                        required
                                        className="w-full px-4 py-3 sm:p-4 bg-gray-50 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-bold border border-transparent focus:border-blue-200 focus:bg-white transition-all text-sm sm:text-base"
                                        value={selectedAcu.parentesco} 
                                        onChange={e => setSelectedAcu({ ...selectedAcu, parentesco: e.target.value })} 
                                    />
                                </div>
                            </div>

                            <button type="submit" className="w-full bg-blue-600 text-white font-black py-4 sm:py-5 rounded-2xl sm:rounded-[2rem] shadow-xl shadow-blue-200 flex items-center justify-center gap-2 hover:bg-blue-700 hover:shadow-blue-300 transition-all active:scale-[0.98] mt-4">
                                <Save size={20} /> Guardar Cambios
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ListaAcudientes;